import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, ShoppingBag, TrendingUp, Repeat, Truck, CheckCircle, XCircle, Star, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDeliveryAnalytics = ({ token, language }) => {
  const [customerData, setCustomerData] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [custRes, delRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/customers`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/deliveries`, { headers })
      ]);
      if (custRes.ok) setCustomerData(await custRes.json());
      if (delRes.ok) setDeliveryData(await delRes.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="space-y-4">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>;

  return (
    <div className="space-y-6">
      {/* Customer Stats */}
      <div>
        <h3 className="text-sm font-bold text-[#004E64] flex items-center gap-2 mb-3">
          <Users className="text-[#2EC4B6]" size={16} />
          {language === 'pt' ? 'Analytics de Clientes' : 'Customer Analytics'}
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Total Clientes' : 'Total Customers'}</p>
              <p className="text-2xl font-bold text-[#004E64]" data-testid="total-customers">{customerData?.total_customers || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Recorrentes' : 'Returning'}</p>
              <p className="text-2xl font-bold text-[#2EC4B6]" data-testid="returning-customers">{customerData?.returning_customers || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Taxa Retencao' : 'Retention Rate'}</p>
              <p className="text-2xl font-bold text-[#FF6B35]">{customerData?.retention_rate || 0}%</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Valor Medio' : 'Avg Order'}</p>
              <p className="text-2xl font-bold text-[#004E64]">{'\u20AC'}{customerData?.avg_order_value || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Top Customers */}
        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{language === 'pt' ? 'Top Clientes' : 'Top Customers'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(customerData?.top_customers || []).slice(0, 10).map((c, i) => (
                <div key={c.user_id} className="flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-[#FF6B35] text-white' : 'bg-[#004E64]/10 text-[#004E64]'}`}>
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#004E64]">{c.name}</p>
                      <p className="text-xs text-[#9FA2B4]">{c.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#FF6B35]">{'\u20AC'}{c.total_spent}</p>
                    <p className="text-xs text-[#9FA2B4]">{c.orders} {language === 'pt' ? 'pedidos' : 'orders'}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Stats */}
      <div>
        <h3 className="text-sm font-bold text-[#004E64] flex items-center gap-2 mb-3">
          <Truck className="text-[#FF6B35]" size={16} />
          {language === 'pt' ? 'Analytics de Entregas' : 'Delivery Analytics'}
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Total Atribuidas' : 'Total Assigned'}</p>
              <p className="text-2xl font-bold text-[#004E64]">{deliveryData?.total_assigned || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Entregues' : 'Delivered'}</p>
              <p className="text-2xl font-bold text-green-600">{deliveryData?.total_delivered || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Sem Entregador' : 'Unassigned'}</p>
              <p className="text-2xl font-bold text-yellow-600">{deliveryData?.unassigned || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Taxa Sucesso' : 'Success Rate'}</p>
              <p className="text-2xl font-bold text-[#2EC4B6]">{deliveryData?.delivery_success_rate || 0}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Driver Performance */}
        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{language === 'pt' ? 'Performance por Entregador' : 'Driver Performance'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(deliveryData?.driver_performance || []).map((d, i) => (
                <div key={d.driver_id} className="flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                      <Truck className="text-[#FF6B35]" size={14} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#004E64]">{d.name}</p>
                      <p className="text-xs text-[#9FA2B4]">{d.vehicle} | <Star size={10} className="inline text-yellow-500" /> {d.rating}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#004E64]">{d.total_deliveries} {language === 'pt' ? 'entregas' : 'deliveries'}</p>
                    <p className="text-xs text-[#9FA2B4]">{d.success_rate}% {language === 'pt' ? 'sucesso' : 'success'}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDeliveryAnalytics;
